import { FC, Fragment, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { useField, useFormikContext } from 'formik';

interface Props {
  clients?: string[];
  disabled?: boolean;
}

const name = 'client_code';

export const ClientCodeMultiselect: FC<Props> = ({
  clients,
  disabled = false,
}) => {
  const [field, meta, helpers] = useField(name);
  const [selected, setSelected] = useState<string[]>(field?.value || []);
  const { errors, touched } = useFormikContext<{ [key: string]: string }>();

  useEffect(() => {
    helpers.setValue(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <div className="col-span-1">
      <Listbox
        value={selected}
        onChange={setSelected}
        name={name}
        disabled={disabled}
        multiple
      >
        {({ open }) => (
          <>
            <Listbox.Label className="block text-sm font-medium mb-1 text-gray-700">
              Client Code
            </Listbox.Label>
            <div className="relative mt-1">
              <Listbox.Button className="relative w-full cursor-default rounded-md bg-white text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm pl-3 pr-10 py-2">
                <span className="block truncate">
                  {selected?.length ? selected.join(', ') : 'Select a Client'}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {clients?.map((x, i) => (
                    <Listbox.Option
                      key={`referral-request-type-${i}`}
                      className={({ active }) =>
                        classNames(
                          active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                          'relative cursor-default select-none py-2 pl-3 pr-9'
                        )
                      }
                      value={x}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={classNames(
                              selected ? 'font-semibold' : 'font-normal',
                              'block truncate'
                            )}
                          >
                            {x}
                          </span>

                          {selected ? (
                            <span
                              className={classNames(
                                active ? 'text-white' : 'text-indigo-600',
                                'absolute inset-y-0 right-0 flex items-center pr-4'
                              )}
                            >
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      {errors[name] && touched[name] ? (
        <p className="mt-2 text-sm text-red-600" id="email-error">
          {errors[name]}
        </p>
      ) : null}
    </div>
  );
};
